import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import extractText from 'flarum/common/utils/extractText';
import DateField from './DateField';
/**
 * Assigns (or clears) the structured event metadata (dates, place, url,
 * poster) linked to a discussion - opened either from the composer
 * (attrs.initialEvent + attrs.onsubmit, discussion doesn't exist yet) or
 * from DiscussionControls on an existing discussion (attrs.discussion +
 * attrs.onsubmit). attrs.onsubmit(event) receives a plain
 * {startDate, endDate, location, url, posterUrl, posterType} object (or
 * null to clear); it may return a Promise (discussion.save()) or nothing
 * (composer just stores it for the pending create request).
 */
export default class EventAssignModal extends Modal {
  // ISO, '' for none, null while what's typed in its field isn't a date
  // (see DateField) - which blocks saving rather than dropping it.
  startDate = '';
  endDate = '';
  location = '';
  url = '';
  posterUrl = null;
  posterType = null;
  posterName = null;
  posterUploading = false;
  posterInput = null;
  loading = false;
  errorMessage = null;
  oninit(vnode) {
    super.oninit(vnode);
    const discussion = this.attrs.discussion;
    const initial = discussion && discussion.event() || this.attrs.initialEvent || null;
    this.startDate = initial && initial.startDate || '';
    this.endDate = initial && initial.endDate || '';
    this.location = initial && initial.location || '';
    this.url = initial && initial.url || '';
    this.posterUrl = initial && initial.posterUrl || null;
    this.posterType = initial && initial.posterType || null;
    this.posterName = null;
    this.posterUploading = false;
    this.loading = false;
    this.errorMessage = null;
  }
  className() {
    return 'Modal--medium EventAssignModal';
  }
  title() {
    return app.translator.trans('champis-events.forum.event.modal_title');
  }
  content() {
    const t = key => app.translator.trans(`champis-events.forum.event.${key}`);
    return m("div", {
      className: "Modal-body"
    }, this.errorMessage && m("div", {
      className: "EventAssignModal-error"
    }, this.errorMessage), m("div", {
      className: "EventAssignModal-field"
    }, m("label", null, t('start_date_label'), m(DateField, {
      value: this.startDate || '',
      onchange: value => {
        this.startDate = value;
      }
    }))), m("div", {
      className: "EventAssignModal-field"
    }, m("label", null, t('end_date_label'), m(DateField, {
      value: this.endDate || '',
      onchange: value => {
        this.endDate = value;
      }
    }))), m("div", {
      className: "EventAssignModal-field"
    }, m("label", null, t('location_label'), m("input", {
      className: "FormControl",
      type: "text",
      placeholder: t('location_placeholder'),
      value: this.location,
      oninput: e => {
        this.location = e.target.value;
      }
    }))), m("div", {
      className: "EventAssignModal-field"
    }, m("label", null, t('url_label'), m("input", {
      className: "FormControl",
      type: "text",
      placeholder: t('url_placeholder'),
      value: this.url,
      oninput: e => {
        this.url = e.target.value;
      }
    }))), m("div", {
      className: "EventAssignModal-field"
    }, m("label", null, t('poster_label')), this.posterUrl ? m("div", {
      className: "EventAssignModal-poster"
    }, this.posterType === 'application/pdf' ? m("a", {
      href: this.posterUrl,
      target: "_blank",
      rel: "noopener noreferrer"
    }, this.posterName || t('poster_view_pdf')) : m("a", {
      href: this.posterUrl,
      target: "_blank",
      rel: "noopener noreferrer"
    }, m("img", {
      className: "EventAssignModal-posterThumb",
      src: this.posterUrl,
      alt: ""
    })), m(Button, {
      className: "Button Button--link",
      onclick: () => this.removePoster()
    }, t('poster_remove'))) : m("div", {
      className: "EventAssignModal-posterUpload"
    }, m(Button, {
      className: "Button ChampisFileButton",
      icon: "fas fa-upload",
      disabled: this.posterUploading,
      onclick: () => this.posterInput?.click()
    }, t('poster_choose_button')), m("input", {
      type: "file",
      accept: "image/*,application/pdf",
      hidden: true,
      "aria-label": extractText(t('poster_label')),
      oncreate: vnode => {
        this.posterInput = vnode.dom;
      },
      onchange: e => {
        const input = e.target;
        this.uploadPoster(input.files?.[0]);
        // The same file picked again (after "Retirer l'affiche") still counts.
        input.value = '';
      }
    }), m("p", {
      className: "EventAssignModal-hint"
    }, t('poster_hint')), this.posterUploading && m("span", {
      className: "EventAssignModal-uploading"
    }, t('poster_uploading')))), m("div", {
      className: "EventAssignModal-footer"
    }, m(Button, {
      className: "Button Button--primary",
      onclick: () => this.submit(),
      loading: this.loading,
      disabled: !this.canSubmit()
    }, t('save_button')), this.attrs.discussion && m(Button, {
      className: "Button Button--link",
      onclick: () => this.submitDelete(),
      disabled: this.loading
    }, t('delete_button'))));
  }
  uploadPoster(file) {
    if (!file) return;
    this.posterUploading = true;
    m.redraw();
    const body = new FormData();
    body.append('files[]', file);
    app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/fof/upload',
      body
    }).then(response => {
      const uploaded = response.data && response.data[0];
      this.posterUploading = false;
      if (uploaded) {
        this.posterUrl = uploaded.attributes.url;
        this.posterType = uploaded.attributes.type;
        this.posterName = uploaded.attributes.baseName;
      }
      m.redraw();
    }).catch(error => {
      this.posterUploading = false;
      this.errorMessage = error.response && error.response.errors && error.response.errors[0] && error.response.errors[0].detail || app.translator.trans('champis-events.forum.event.error');
      m.redraw();
    });
  }
  removePoster() {
    this.posterUrl = null;
    this.posterType = null;
    this.posterName = null;
  }
  canSubmit() {
    if (this.loading || this.posterUploading) return false;
    return !!this.startDate && this.endDate !== null;
  }
  buildPayload() {
    return {
      startDate: this.startDate || '',
      endDate: this.endDate || null,
      location: this.location || null,
      url: this.url || null,
      posterUrl: this.posterUrl,
      posterType: this.posterType
    };
  }
  submit() {
    if (this.endDate && this.startDate && this.endDate < this.startDate) {
      this.errorMessage = app.translator.trans('champis-events.forum.event.error_end_before_start');
      return;
    }
    this.run(this.buildPayload());
  }
  submitDelete() {
    this.run(null);
  }
  run(payload) {
    this.loading = true;
    this.errorMessage = null;
    m.redraw();
    const result = this.attrs.onsubmit(payload);
    if (result && typeof result.then === 'function') {
      result.then(() => this.hide(), error => {
        this.loading = false;
        this.errorMessage = error && error.message || app.translator.trans('champis-events.forum.event.error');
        m.redraw();
      });
    } else {
      this.hide();
    }
  }
}
flarum.reg.add('champis-events', 'forum/components/EventAssignModal', EventAssignModal);